import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../app/store';
import Optional from '../../Optional/Optional';
import { useJwtHeaders } from '../../../hooks/useJwtHeaders';
import axios from 'axios';
import { saveEsrogAction } from '../../../actions/saveEsrogAction';
import './CreateEsrogsPage.scss';

interface EsrogItem {
  id: number;
  kind: string;
  pitum: boolean;
  year?: string;
  active?: boolean;
}

const CreateEsrogsPage: FC = () => {
  const dispatch = useDispatch();
  const headers = useJwtHeaders();
  const admin = useSelector<RootState, boolean>(({ user }) => user.admin!);

  const [esrogs, setEsrogs] = useState<EsrogItem[]>([]);
  const [kind, setKind] = useState<string>('');
  const [pitum, setPitum] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchEsrogs = () => {
    axios
      .get('/api/v1/admin/esrogs', headers)
      .then(({ data }) => {
        setEsrogs(data);
      })
      .catch(() => {
        axios.get('/api/v1/available-items', headers).then(({ data }) => {
          if (data && data.esrogs) {
            setEsrogs(data.esrogs);
          }
        });
      });
  };

  useEffect(() => {
    fetchEsrogs();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!kind.trim()) return;

    setLoading(true);
    dispatch(
      saveEsrogAction(kind, pitum, headers, () => {
        setKind('');
        setPitum(false);
        setLoading(false);
        fetchEsrogs();
      })
    );
  };

  const handleToggleActive = (id: number, newActive: boolean) => {
    setEsrogs(prev => prev.map(item => (item.id === id ? { ...item, active: newActive } : item)));
    axios.patch(`/api/v1/admin/esrogs/${id}`, { active: newActive }, headers).catch(() => {
      setEsrogs(prev => prev.map(item => (item.id === id ? { ...item, active: !newActive } : item)));
    });
  };

  const handleDelete = (id: number) => {
    if (!window.confirm('Are you sure you want to delete this Esrog?')) return;
    setEsrogs(prev => prev.filter(item => item.id !== id));
    axios.delete(`/api/v1/admin/esrogs/${id}`, headers).catch(() => {
      alert('Failed to delete Esrog');
      fetchEsrogs();
    });
  };

  return (
    <div className="container admin-item-page">
      <Optional renderIf={admin}>
        <h3>Admin: Manage & Create Esrogim</h3>

        <div className="form-section">
          <h5 className="mb-3">Add New Esrog</h5>
          <form onSubmit={handleSubmit} className="inline-form">
            <div style={{ flex: '1 1 200px', maxWidth: '300px' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Type (e.g. Yanover)"
                value={kind}
                onChange={e => setKind(e.target.value)}
                required
              />
            </div>
            <div className="form-check m-0">
              <input
                type="checkbox"
                className="form-check-input"
                id="pitumCheck"
                checked={pitum}
                onChange={e => setPitum(e.target.checked)}
              />
              <label className="form-check-label ms-1" htmlFor="pitumCheck">
                With Pitum
              </label>
            </div>
            <div className="submit-btn-container">
              <button type="submit" className="btn btn-success" disabled={!kind.trim() || loading}>
                {loading ? 'Saving...' : 'Save Esrog'}
              </button>
            </div>
          </form>
        </div>

        <h5>Current Esrogim ({esrogs.length})</h5>
        <table className="table table-hover table-striped">
          <thead>
            <tr>
              <th>ID</th>
              <th>Kind</th>
              <th>Pitum</th>
              <th>Year</th>
              <th>Active</th>
              <th style={{ width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {esrogs.map(esrog => (
              <tr key={`esrog-${esrog.id}`}>
                <td>{esrog.id}</td>
                <td>{esrog.kind}</td>
                <td>{esrog.pitum ? 'Yes' : 'No'}</td>
                <td>{esrog.year || new Date().getFullYear()}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={esrog.active ?? true}
                    onChange={e => handleToggleActive(esrog.id, e.target.checked)}
                  />
                </td>
                <td className="text-center">
                  <i
                    className="fa delete-icon"
                    title="Delete Esrog"
                    onClick={() => handleDelete(esrog.id)}>
                    &#xf014;
                  </i>
                </td>
              </tr>
            ))}
            {esrogs.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-muted">
                  No Esrogim found for current year.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Optional>
      <Optional renderIf={!admin}>
        <b>YOU MUST BE AN ADMIN TO VIEW THIS PAGE</b>
      </Optional>
    </div>
  );
};

export default CreateEsrogsPage;
